import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { toastCopyableError } from "./copyable-error-toast";
import ProgramGenerationChatModal from "./ProgramGenerationChatModal";
import { parseYYYYMMDDToLocalDate } from "@/lib/date-only";
import { buildChatErrorCopyOptions } from "@/lib/format-error-for-copy";
import type { WorkoutAssigned } from "@/integrations/backend/workout-assigned";
import type {
  AIGenerationChatMessage,
  AIGenerationConversation,
  AssignedWorkoutEditPreviewPayload,
} from "@/integrations/backend/program-generation";
import {
  confirmAssignedWorkoutEdit,
  generateAssignedWorkoutEdit,
  getAssignedWorkoutEditChat,
  parseAssignedWorkoutEditAssistantContent,
  restartAssignedWorkoutEdit,
  sendAssignedWorkoutEditMessage,
} from "@/integrations/backend/program-generation";

const ASSIGNED_WORKOUT_EDIT_SEED_PREFIX = "Current workout snapshot (reference only):";

type EditAssignedWorkoutWithAIDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  workout: WorkoutAssigned | null;
  onWorkoutUpdated?: () => Promise<void> | void;
  onOpenManualEdit?: (assignedWorkoutId: string, clientId: string, assignmentDate: Date | null) => void;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const assistantMessageCount = (conversation: AIGenerationConversation | null): number =>
  (conversation?.messages ?? []).filter((m) => m.role === "assistant").length;

async function waitForAssistantResponse({
  conversationId,
  initialAssistantCount,
  fetchConversation,
  applyConversation,
}: {
  conversationId: string | null;
  initialAssistantCount: number;
  fetchConversation: (conversationId: string) => Promise<AIGenerationConversation>;
  applyConversation: (conversation: AIGenerationConversation) => void;
}): Promise<AIGenerationConversation> {
  if (!conversationId) {
    throw new Error("conversation_id is required");
  }
  const timeoutMs = 600_000;
  const intervalMs = 2_000;
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    await sleep(intervalMs);
    const next = await fetchConversation(conversationId);
    applyConversation(next);
    if (assistantMessageCount(next) > initialAssistantCount) {
      return next;
    }
  }
  throw new Error("Timed out waiting for AI response");
}

export default function EditAssignedWorkoutWithAIDialog({
  open,
  onOpenChange,
  clientId,
  workout,
  onWorkoutUpdated,
  onOpenManualEdit,
}: EditAssignedWorkoutWithAIDialogProps) {
  const [conversation, setConversation] = useState<AIGenerationConversation | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !workout?.id) {
      if (!open) {
        setConversation(null);
        setInput("");
        setBusy(false);
      }
      return;
    }

    let cancelled = false;
    const startConversation = async () => {
      setBusy(true);
      let errorConversation = conversation;
      const applyConversation = (next: AIGenerationConversation) => {
        errorConversation = next;
        setConversation(next);
      };
      try {
        const next = await generateAssignedWorkoutEdit(clientId, workout.id);
        if (!cancelled) {
          applyConversation(next);
          setInput("");
        }
      } catch (error: unknown) {
        if (!cancelled) {
          toastCopyableError(error, "Failed to start AI workout editing", buildChatErrorCopyOptions(error, errorConversation));
          onOpenChange(false);
        }
      } finally {
        if (!cancelled) {
          setBusy(false);
        }
      }
    };

    void startConversation();
    return () => {
      cancelled = true;
    };
  }, [open, workout?.id, clientId, onOpenChange]);

  const latestAssistant = useMemo<AIGenerationChatMessage | null>(() => {
    const assistants = (conversation?.messages ?? []).filter((m) => m.role === "assistant");
    return assistants.length > 0 ? assistants[assistants.length - 1] ?? null : null;
  }, [conversation]);

  const latestPreview = useMemo<AssignedWorkoutEditPreviewPayload | null>(() => {
    const parsedPreview = conversation?.parsed_preview;
    if (
      parsedPreview &&
      typeof parsedPreview === "object" &&
      "groupings" in parsedPreview &&
      "duration_minutes" in parsedPreview &&
      "date" in parsedPreview
    ) {
      return parsedPreview as AssignedWorkoutEditPreviewPayload;
    }
    return parseAssignedWorkoutEditAssistantContent(latestAssistant?.content ?? "");
  }, [conversation, latestAssistant]);

  const hasUserMessage = useMemo(
    () => (conversation?.messages ?? []).some((message) => message.role === "user"),
    [conversation]
  );

  const canConfirm = Boolean(
    workout?.id &&
      latestPreview &&
      hasUserMessage &&
      latestAssistant &&
      latestAssistant.role === "assistant" &&
      !latestAssistant.content.startsWith(ASSIGNED_WORKOUT_EDIT_SEED_PREFIX)
  );

  const exerciseNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const grouping of workout?.groupings ?? []) {
      for (const exercise of grouping.exercises) {
        if (exercise.exercise_id && exercise.exercise_name) {
          map[exercise.exercise_id] = exercise.exercise_name;
        }
      }
    }
    return map;
  }, [workout]);

  const handleSend = async () => {
    if (!workout?.id) return;
    const conversationId = conversation?.conversation_id;
    const content = input.trim();
    if (!conversationId || !content) return;

    const optimisticMessage: AIGenerationChatMessage = {
      id: `optimistic-${Date.now()}`,
      profile_id: clientId,
      chat_type: "assigned_workout_edit",
      conversation_id: conversationId,
      role: "user",
      content,
      created_at: null,
      updated_at: null,
    };

    setConversation((prev) =>
      prev ? { ...prev, messages: [...prev.messages, optimisticMessage] } : prev
    );
    setInput("");
    setBusy(true);
    let errorConversation = conversation;
    const applyConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setConversation(next);
    };
    try {
      const next = await sendAssignedWorkoutEditMessage(clientId, workout.id, {
        conversation_id: conversationId,
        content,
      });
      applyConversation(next);
      await waitForAssistantResponse({
        conversationId: next.conversation_id,
        initialAssistantCount: assistantMessageCount(next),
        fetchConversation: (nextConversationId) =>
          getAssignedWorkoutEditChat(clientId, workout.id, nextConversationId),
        applyConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to send message", buildChatErrorCopyOptions(error, errorConversation, content));
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              messages: prev.messages.filter((message) => message.id !== optimisticMessage.id),
            }
          : prev
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRestart = async () => {
    if (!workout?.id) return;
    setBusy(true);
    let errorConversation = conversation;
    const applyConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setConversation(next);
    };
    try {
      const next = await restartAssignedWorkoutEdit(clientId, workout.id);
      applyConversation(next);
      setInput("");
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to restart conversation", buildChatErrorCopyOptions(error, errorConversation));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!workout?.id) return;
    const conversationId = conversation?.conversation_id;
    if (!conversationId) return;
    setBusy(true);
    try {
      const updatedWorkout = await confirmAssignedWorkoutEdit(clientId, workout.id, conversationId);
      await onWorkoutUpdated?.();
      onOpenChange(false);
      toast.success("Workout updated");
      onOpenManualEdit?.(
        workout.id,
        clientId,
        updatedWorkout.date ? parseYYYYMMDDToLocalDate(updatedWorkout.date) : null
      );
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to finalize workout edit", buildChatErrorCopyOptions(error, conversation));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProgramGenerationChatModal
      open={open}
      onOpenChange={onOpenChange}
      title={workout ? `Edit ${workout.name} with AI` : "Edit Workout with AI"}
      conversation={conversation}
      input={input}
      onInputChange={setInput}
      busy={busy}
      onSend={handleSend}
      onRestart={handleRestart}
      onConfirm={handleConfirm}
      canConfirm={canConfirm}
      confirmLabel="Finalize Workout Edit"
      assignedWorkoutPreview={latestPreview}
      exerciseNameMap={exerciseNameMap}
      parseError={conversation?.parse_error ?? null}
      inputPlaceholder="Describe how you want this workout changed..."
    />
  );
}
